/**
 * Builds a CSV-ready 2D array for agenda exports. Two output shapes,
 * selected by the `shape` option:
 *
 * - "matrix" — one row per (session × attendee) pair. Columns include
 *   attendee name/email/role. Use when the organizer wants to see
 *   who is seeing what across a cohort. Default for non-user filters.
 *   Not available for single-user filter (use "personal" instead).
 *
 * - "personal" — one row per session a single user sees. Columns:
 *   Session, Date, Start, End, Location, Type, Audience. Requires
 *   audience.kind === "user". Prepends a meta row with the user's
 *   display name.
 *
 * Audience column (ADR-120): each session points at one `audienceId`.
 * `formatAudienceLabel` resolves it through the audienceById map and
 * descends one level into anonymous compositions, so the CSV reads
 * the way the admin UI does, named audience by name, anonymous
 * composition by joined-include label list, personal audience by the
 * user's name.
 *
 * Visibility for the matrix is sourced from `session.targetedPersonIds[]`,
 * the CF-maintained access cache, personId-keyed (ADR-137). Pending
 * people appear in the matrix because they're person-resolved even
 * before they claim — the security rule is what gates the actual
 * client read, not the cache contents.
 *
 * Rows are joined against `persons` and `eventParticipants` directly.
 */

import {
  formatAudienceLabel,
  type Audience,
  type AudienceLabelUser,
  type Session,
  type EventParticipant,
  type Person,
  type SessionTypeConfig,
} from "@event-planner/shared";

export type AudienceSpec =
  | { kind: "all" }
  | { kind: "group"; groupId: string }
  | { kind: "user"; userId: string };

export type CsvShape = "matrix" | "personal";

interface AgendaCsvOptions {
  sessions: Session[];
  /** Active participants for the event. */
  participants: EventParticipant[];
  /** Persons for the active participants, keyed by `person.id`. */
  persons: Person[];
  sessionTypes: SessionTypeConfig[];
  /** All audiences in the event keyed by id. */
  audienceById: Map<string, Audience>;
  /** Person lookup for resolving personal-audience labels. */
  userById: Map<string, AudienceLabelUser>;
  audience: AudienceSpec;
  shape: CsvShape;
  timezone: string;
}

function formatTimestamp(
  ts: { toDate?: () => Date; seconds?: number },
  timezone: string,
): string {
  let date: Date;
  if (typeof ts.toDate === "function") {
    date = ts.toDate();
  } else if (typeof ts.seconds === "number") {
    date = new Date(ts.seconds * 1000);
  } else {
    return "";
  }
  return date.toLocaleString("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function audienceCell(
  session: Session,
  audienceById: Map<string, Audience>,
  userById: Map<string, AudienceLabelUser>,
): string {
  if (!session.audienceId) return "—";
  const audience = audienceById.get(session.audienceId);
  return formatAudienceLabel(audience, audienceById, userById);
}

/**
 * True when a session's audience matches the picked named-audience id,
 * either by direct reference or by anonymous-composition include.
 * Mirrors the SessionListPage filter walk.
 */
function sessionMatchesGroupId(
  session: Session,
  groupId: string,
  audienceById: Map<string, Audience>,
): boolean {
  if (!session.audienceId) return false;
  if (session.audienceId === groupId) return true;
  const aud = audienceById.get(session.audienceId);
  if (!aud) return false;
  if (aud.name === null && aud.system === false) {
    return aud.members.includedAudienceIds.includes(groupId);
  }
  return false;
}

/**
 * Build a personal agenda CSV for a single user, one row per session
 * they see.
 *
 * Sources visibility from the session's CF-maintained
 * `targetedPersonIds[]` cache (ADR-137). The `userId` parameter is a
 * personId — same string everywhere, claim-state-agnostic.
 */
function buildPersonalAgenda(opts: AgendaCsvOptions, userId: string): string[][] {
  const person = opts.persons.find((p) => p.id === userId);
  const typeLookup = new Map(opts.sessionTypes.map((st) => [st.id, st.name]));

  const visibleSessions = opts.sessions.filter((s) =>
    s.targetedPersonIds.includes(userId),
  );

  const header = [
    "Session",
    "Date",
    "Start",
    "End",
    "Location",
    "Type",
    "Audience",
  ];

  const rows = visibleSessions.map((s) => [
    s.title,
    s.date || "",
    formatTimestamp(s.startTime, opts.timezone),
    formatTimestamp(s.endTime, opts.timezone),
    s.locationLabel || "",
    typeLookup.get(s.sessionTypeId) || s.sessionTypeId,
    audienceCell(s, opts.audienceById, opts.userById),
  ]);

  const nameRow = [
    `Agenda for: ${person?.displayName ?? person?.email ?? userId}`,
    "",
    "",
    "",
    "",
    "",
    "",
  ];

  return [nameRow, header, ...rows];
}

/**
 * Build a full visibility matrix CSV, one row per (session × user)
 * tuple.
 *
 * Sources visibility from the session's CF-maintained
 * `targetedPersonIds[]` cache. For audience.kind === "group" we further
 * restrict to sessions whose audience matches the picked named-
 * audience (direct reference or via anonymous-composition include).
 */
function buildVisibilityMatrix(opts: AgendaCsvOptions): string[][] {
  const personLookup = new Map(opts.persons.map((p) => [p.id, p]));
  const participantByPersonId = new Map(
    opts.participants.map((p) => [p.personId, p]),
  );
  const typeLookup = new Map(opts.sessionTypes.map((st) => [st.id, st.name]));

  const header = [
    "Session",
    "Date",
    "Start",
    "End",
    "Location",
    "Type",
    "Audience",
    "Attendee",
    "Email",
    "Role",
  ];

  const rows: string[][] = [];

  for (const session of opts.sessions) {
    if (
      opts.audience.kind === "group" &&
      !sessionMatchesGroupId(session, opts.audience.groupId, opts.audienceById)
    ) {
      continue;
    }

    const audienceLabel = audienceCell(
      session,
      opts.audienceById,
      opts.userById,
    );

    for (const personId of session.targetedPersonIds) {
      const person = personLookup.get(personId);
      const participant = participantByPersonId.get(personId);

      rows.push([
        session.title,
        session.date || "",
        formatTimestamp(session.startTime, opts.timezone),
        formatTimestamp(session.endTime, opts.timezone),
        session.locationLabel || "",
        typeLookup.get(session.sessionTypeId) || session.sessionTypeId,
        audienceLabel,
        person?.displayName ?? personId,
        person?.email ?? "",
        participant?.role ?? "",
      ]);
    }
  }

  return [header, ...rows];
}

/**
 * Entry point. Dispatches by shape. "personal" requires
 * audience.kind === "user", validated at the dialog layer; here we
 * fall back to matrix to avoid throwing mid-export if the caller
 * invariants ever slip.
 */
export function buildAgendaCsvRows(opts: AgendaCsvOptions): string[][] {
  if (opts.shape === "personal" && opts.audience.kind === "user") {
    return buildPersonalAgenda(opts, opts.audience.userId);
  }
  return buildVisibilityMatrix(opts);
}
